import type { DisciplioSettings } from "../disciplio/types";
import { BROWSER_STORAGE_KEYS, readBrowserStorageConsent } from "../browser-storage";

export function getStoredLanguage(): string {
  if (typeof localStorage === "undefined") return "en";
  if (readBrowserStorageConsent() !== "accepted") return "en";
  return localStorage.getItem(BROWSER_STORAGE_KEYS.language) ?? "en";
}

export function normalizeLanguageForApi(value: string): string {
  const language = (value || "en").toLowerCase();
  if (language.startsWith("pl")) return "pl";
  if (language.startsWith("uk")) return "uk";
  return "en";
}

export function currentWeekStartKey(date = new Date()): string {
  const copy = new Date(date);
  const day = copy.getDay() || 7;
  copy.setDate(copy.getDate() - day + 1);
  copy.setHours(0, 0, 0, 0);
  return dateKey(copy);
}

export function dateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseApiList(value?: string | null): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function parseJsonArray(value?: string | null): unknown[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isDateRange(value: unknown): value is DisciplioSettings["streakFreeDateRanges"][number] {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { id?: unknown; start?: unknown; end?: unknown };
  return typeof candidate.id === "string" && isDateKey(candidate.start) && isDateKey(candidate.end);
}

export function isDateKey(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function clampHour(value: unknown, fallback: number): number {
  const hour = Number(value);
  if (!Number.isFinite(hour)) return fallback;
  return Math.max(0, Math.min(23, Math.round(hour)));
}

export function normalizeTimeForApi(value?: string): string {
  return normalizeTimeForInput(value, "00:00");
}

export function normalizeTimeForInput(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return fallback;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function looksLikeUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}
